import React from 'react';
import { Alert, Button, Card, Col, Row, Space, Tag, Typography } from 'antd';
import { CustomerServiceOutlined, LoginOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { DataTable } from '../../components';
import { useSupportTickets } from '../../hooks/useSupportTickets';

const { Title } = Typography;

type TicketStatus = 'open' | 'in_progress' | 'resolved';

interface SupportTicket {
  id: string;
  tenantId: string;
  tenantName: string;
  subject: string;
  status: TicketStatus;
  priority: 'low' | 'medium' | 'high';
  updatedAt: string;
}

const statusColor: Record<TicketStatus, string> = {
  open: 'red',
  in_progress: 'blue',
  resolved: 'green',
};

const priorityColor: Record<string, string> = {
  low: 'default',
  medium: 'gold',
  high: 'volcano',
};

export const SupportTickets: React.FC = () => {
  const navigate = useNavigate();
  const {
    tickets,
    loading,
    error,
    summary,
    meta,
    filters,
    setFilters,
    refetch,
  } = useSupportTickets({ page: 1, limit: 20 });

  const columns: ColumnsType<SupportTicket> = [
    { title: 'Ticket', dataIndex: 'id', key: 'id', width: 120 },
    {
      title: 'Tenant',
      dataIndex: 'tenantName',
      key: 'tenantName',
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <span>{value}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{record.tenantId}</span>
        </Space>
      ),
    },
    { title: 'Subject', dataIndex: 'subject', key: 'subject' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: TicketStatus) => <Tag color={statusColor[value]}>{value.toUpperCase()}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (value) => <Tag color={priorityColor[value]}>{String(value).toUpperCase()}</Tag>,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Support Tickets</Title>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={8}><Card><b>Open:</b> {summary.open}</Card></Col>
        <Col span={8}><Card><b>In Progress:</b> {summary.inProgress}</Card></Col>
        <Col span={8}><Card><b>Resolved:</b> {summary.resolved}</Card></Col>
      </Row>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load support tickets"
          description={error.message}
          action={<Button size="small" onClick={refetch}>Retry</Button>}
        />
      )}

      <DataTable
        title="Support Queue"
        data={tickets}
        columns={columns}
        loading={loading}
        rowKey="id"
        searchable
        searchPlaceholder="Search tickets..."
        onSearch={(value) => setFilters({ ...filters, search: value || undefined, page: 1 })}
        onRefresh={refetch}
        emptyText="No support tickets"
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: (page, pageSize) => setFilters({ ...filters, page, limit: pageSize }),
        }}
        actionColumn={{
          width: 120,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'impersonate',
              label: 'Impersonate Tenant',
              icon: <LoginOutlined />,
              onClick: () => navigate(`/tenants/${record.tenantId}`),
            },
            {
              key: 'misa',
              label: 'Open MISA Dashboard',
              icon: <SyncOutlined />,
              onClick: () => navigate(`/misa?tenantId=${encodeURIComponent(record.tenantId)}`),
            },
            {
              key: 'queue',
              label: 'Open Queue Monitor',
              icon: <CustomerServiceOutlined />,
              onClick: () => navigate('/system/queues'),
            },
          ],
        }}
      />
    </div>
  );
};

export default SupportTickets;
